import Form from "../../../formability/form_builder/models/form";
import FieldPresenceValidator from "../navigableValidators/fieldPresenceValidator";
import UrlValidator from "../navigableValidators/urlValidator";
import EmbeddedFieldValidator from "../navigableValidators/embeddedFieldValidator";
import NavigableValidation, {ErrorNavOpts} from "./navigableValidation";
import EmbeddedCollectionValidator from "../navigableValidators/embeddedCollectionValidator";
import MinMaxFieldValidator from "../navigableValidators/minMaxFieldValidator";
import {minMaxType} from "~/enums/minMaxValidatorType"
import {FormDueDateType} from "~/enums/formDueDateType"
import BrokenWorkFlowStepsValidator, {BrokenWorkflowErrorType} from "../navigableValidators/brokenWorkflowStepsValidator";
import {ValidationModalSection} from "~/models/navigableValidations/validationSections";
import {IErrorNavOpts} from "~/models/validationError";
import ModelMapping from "~/models/mapping/modelMapping";
import LengthValidator from "~/models/navigableValidators/lengthValidator";
import FormElementIndexCapValidator from "~/models/navigableValidators/formElementIndexCapValidator";
import scrollToElement from "~/helpers/scrollToElement";
import {IndexType} from "~/enums/formElementIndexType";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";

export default class FormBuilderValidation extends NavigableValidation<Form> {
  validators = [
    //  Public Profile Fields
    new FieldPresenceValidator({
      validatorKey: 'short_description',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_specified_date',
      displayName: 'Specified Date',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinSpecifiedDate(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_specified_window_open_date',
      displayName: 'Submission Window Begins',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinSpecifiedTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_specified_window_close_date',
      displayName: 'Submission Window Closes',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinSpecifiedTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_open_date_day',
      displayName: 'Recurring Submission Window Begins Day',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_open_date_month',
      displayName: 'Recurring Submission Window Begins Month',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_open_date_time_of_day',
      displayName: 'Recurring Submission Window Begins Time of Day',
      conditional: (model: Form) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model) && model.include_time
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_open_date_time_am_or_pm',
      displayName: 'Recurring Submission Window Begins AM or PM',
      conditional: (model: Form) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model) && model.include_time
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_close_date_day',
      displayName: 'Recurring Submission Window Closes Day',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_close_date_month',
      displayName: 'Recurring Submission Window Closes Month',
      conditional: (model: ModelMapping) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model)
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_close_date_time_of_day',
      displayName: 'Recurring Submission Window Closes Time of Day',
      conditional: (model: Form) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model) && model.include_time
      },
      section: ValidationModalSection.PublicProfile
    }),
    new FieldPresenceValidator({
      validatorKey: 'due_window_close_date_time_am_or_pm',
      displayName: 'Recurring Submission Window Closes AM or PM',
      conditional: (model: Form) => {
        return this.hasPublicProfile(model) && this.withinRecurringTimePeriod(model) && model.include_time
      },
      section: ValidationModalSection.PublicProfile
    }),
    new MinMaxFieldValidator({
      validatorKey: 'min_completion_time',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
      min_field: 'min_completion_time',
      max_field: 'max_completion_time',
      minMaxType: minMaxType.duration,
      min_field_unit: 'min_completion_time_unit',
      max_field_unit: 'max_completion_time_unit'
    }),
    new MinMaxFieldValidator({
      validatorKey: 'min_approval_time',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
      min_field: 'min_approval_time',
      max_field: 'max_approval_time',
      minMaxType: minMaxType.duration,
      min_field_unit: 'min_approval_time_unit',
      max_field_unit: 'max_approval_time_unit'
    }),
    new MinMaxFieldValidator({
      validatorKey: 'min_cost',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
      min_field: 'min_cost',
      max_field: 'max_cost',
      minMaxType: minMaxType.currency,
      conditional_field: 'cost_has_range'
    }),
    new FieldPresenceValidator({
      section: ValidationModalSection.PublicProfile,
      validatorKey: "apply_section_header",
    }),
    new FieldPresenceValidator({
      section: ValidationModalSection.PublicProfile,
      validatorKey: "apply_sub_text",
    }),
    new FieldPresenceValidator({
      section: ValidationModalSection.PublicProfile,
      validatorKey: "apply_button_label",
    }),
    new MinMaxFieldValidator({
      validatorKey: 'due_specified_window_open_date',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
      min_field: 'due_specified_window_open_date',
      max_field: 'due_specified_window_close_date',
      minMaxType: minMaxType.dateTime,
      min_field_unit: 'due_specified_window_open_date',
      max_field_unit: 'due_specified_window_close_date'
    }),
    new MinMaxFieldValidator({
      validatorKey: 'due_window_open_date',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
      min_field: 'recurringWindowOpenDate',
      max_field: 'recurringWindowCloseDate',
      minMaxType: minMaxType.dateTime,
      min_field_unit: 'recurringWindowOpenDate',
      max_field_unit: 'recurringWindowCloseDate'
    }),
    new UrlValidator({
      validatorKey: 'external_pdf_url',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile
    }),
    new EmbeddedFieldValidator({
      validatorKey: 'seo',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
    }, this.model),
    new EmbeddedCollectionValidator({
      validatorKey: 'resources',
      section: ValidationModalSection.PublicProfile,
      conditional: this.hasPublicProfile,
    }, this.model),

    // Workflow Steps
    new EmbeddedCollectionValidator({
      validatorKey: 'workflow_steps',
      section: ValidationModalSection.Workflow
    }, this.model),
    new BrokenWorkFlowStepsValidator({
      validatorKey: "brokenWorkflowStepValidator",
      section: ValidationModalSection.Workflow
    }),

    // Index Validation
    new FormElementIndexCapValidator({
      validatorKey: "form_element_text_index_cap",
      section: ValidationModalSection.Layout,
      beforeReveal: async  () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToLastPropOfIndexType(IndexType.Keyword);
        })
      },
    }, IndexType.Keyword),
    new FormElementIndexCapValidator({
      validatorKey: "form_element_date_index_cap",
      section: ValidationModalSection.Layout,
      beforeReveal: async  () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToLastPropOfIndexType(IndexType.Date);
        })
      },
    }, IndexType.Date),
    new FormElementIndexCapValidator({
      validatorKey: "form_element_date_index_cap",
      section: ValidationModalSection.Layout,
      beforeReveal: async  () => {
        await window.requestAnimationFrame(async () => {
          await this.scrollToLastPropOfIndexType(IndexType.Double);
        })
      },
    }, IndexType.Double),

    // Comms Panel Settings
    new FieldPresenceValidator({
      validatorKey: 'communications_panel_group_id',
      section: ValidationModalSection.Settings,
      conditional: (form: Form) => form.communications_panel_enabled,
    }),
    new FieldPresenceValidator({
      validatorKey: 'communications_panel_sla_amount',
      section: ValidationModalSection.Settings,
      conditional: (form: Form) => form.communications_panel_enabled,
    }),
    new FieldPresenceValidator({
      validatorKey: 'communications_panel_sla_unit',
      section: ValidationModalSection.Settings,
      conditional: (form: Form) => form.communications_panel_enabled,
    }),
    new FieldPresenceValidator({
      validatorKey: 'communications_panel_sla_user_ids',
      section: ValidationModalSection.Settings,
      conditional: (form: Form) => form.communications_panel_enabled,
    }),
    new LengthValidator(0, 120, {
      validatorKey: 'communications_panel_placeholder_message',
      section: ValidationModalSection.Settings,
      conditional: (form: Form) => form.communications_panel_enabled,
    }),
  ];

  errorNavOpts(validator, validatorKey: string, value, errorType: string): IErrorNavOpts {
    const anchorHash = this.getAnchorHash(validatorKey, value) || "";
    const path = this.getPath(validatorKey, value) || "" ;
    let query;
    if (validatorKey === 'brokenWorkflowStepValidator') {
      query = this.buildQuery(errorType, value)
    } else {
      query = this.buildQuery(validatorKey, value)
    }

    return new ErrorNavOpts({
      path: path,
      hash: anchorHash,
      query: query,
      elementClientId: "",
      targetElementId: "",
      containerElementSelector: "",
      beforeReveal: validator.beforeReveal || null,
      afterReveal: null
    })
  };

  protected getAnchorHash(field: string, value?): string {
    switch (field) {
      case BrokenWorkflowErrorType.WorkflowError:
      case BrokenWorkflowErrorType.LoopError:
        return ""
      default:
        return super.getAnchorHash(field, value);
    }
  }

  protected buildQuery(validatorKey, value?): {} {
    let q = super.buildQuery(validatorKey, value);
    if (validatorKey === BrokenWorkflowErrorType.LoopError) {
      q = Object.assign(q, {
        stepId: value.client_id,
        brokenWorkflowId: value.client_id
      })
    } else if (validatorKey === BrokenWorkflowErrorType.WorkflowError) {
      q = Object.assign(q, {brokenWorkflow: true});
    }
    return q;
  }

  hasPublicProfile(model): boolean {
    return model['has_form_profile_page']
  }

  withinSpecifiedDate(model): boolean {
    return model['application_due_type'] === FormDueDateType.specifiedDate
  }

  withinSpecifiedTimePeriod(model): boolean {
    return model['application_due_type'] === FormDueDateType.withinSpecifiedTimePeriod
  }

  withinRecurringTimePeriod(model): boolean {
    return model['application_due_type'] === FormDueDateType.withinTimePeriod
  }

  async scrollToLastPropOfIndexType(indexType: IndexType): Promise<JQuery> {
    const indexedPropertiesOfType = this.model.indexedElements.filter(prop=>{
      return FormElement.getElementIndexType(prop) === indexType
    }).sort((a, b) => a.sort_order - b.sort_order)

    const lastProperty = indexedPropertiesOfType[indexedPropertiesOfType.length-1]
    const $label = $(`[data-id="${lastProperty.client_id}"]`);
    await scrollToElement({
      offset: -20,
      $el: $label,
      $container: $("#fe-listing").parent(),
      relative: true
    })
    $label.trigger("click")
    return $label;
  }
}